/**
 * DemandDiscussion — the comment surface for everyone who is not council.
 *
 * Comments were council-only on both sides until #995's comment cell was
 * decided (2026-09-22): anyone who can open a demand may post on it, and
 * reads their own messages plus any that @mention them. Council keeps the
 * Timeline tab, which shows the discussion alongside stage history and field
 * edits — internals this card deliberately does not carry.
 *
 * The server decides what comes back, so a caretaker who is not council sees
 * the whole discussion here without this component knowing the rule.
 */

import { useState } from 'react'
import { Button, Spinner, Textarea, makeStyles, tokens } from '@fluentui/react-components'
import { useComments, useCreateComment } from '../hooks/useComments'
import { UserAvatar } from './UserAvatar'
import { formatRelative } from '../lib/relativeTime'
import { apiErrorMessage } from '../lib/apiError'

const useStyles = makeStyles({
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  head: { display: 'flex', flexDirection: 'column', gap: '2px' },
  title: { fontWeight: tokens.fontWeightSemibold, fontSize: '15px' },
  note: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  entry: { display: 'flex', gap: tokens.spacingHorizontalS },
  entryBody: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: '2px' },
  entryMeta: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  entryAuthor: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  entryText: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '14px' },
  composer: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  error: { color: tokens.colorPaletteRedForeground1, fontSize: '12px' },
})

interface DemandDiscussionProps {
  demandId: number
}

export function DemandDiscussion({ demandId }: DemandDiscussionProps) {
  const styles = useStyles()
  const { data: comments, isLoading, isError } = useComments(demandId)
  const create = useCreateComment()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  function send() {
    const body = draft.trim()
    if (!body || create.isPending) return
    setError(null)
    create.mutate(
      { demandId, body: { body } },
      {
        onSuccess: () => setDraft(''),
        onError: (err: unknown) => setError(apiErrorMessage(err, 'Could not post your message')),
      },
    )
  }

  return (
    <section className={styles.card} data-testid="demand-discussion">
      <div className={styles.head}>
        <span className={styles.title}>Discussion</span>
        <span className={styles.note}>
          Council reads all of it. You see yours and mentions of you.
        </span>
      </div>

      {isLoading && <Spinner size="tiny" label="Loading messages" />}
      {/* A failed load must not read as "nobody has said anything" (#1247). */}
      {isError && (
        <span className={styles.error} data-testid="discussion-load-error">
          Could not load the messages. Reload the page to try again.
        </span>
      )}
      {!isLoading && !isError && (comments?.length ?? 0) === 0 && (
        <span className={styles.note} data-testid="discussion-empty">
          Nothing here yet. Anything you write goes to the council.
        </span>
      )}

      {(comments?.length ?? 0) > 0 && (
        <div className={styles.list}>
          {comments!.map((c) => (
            <div key={c.id} className={styles.entry} data-testid={`discussion-comment-${c.id}`}>
              <span aria-hidden="true">
                <UserAvatar name={c.author_name || 'Unknown'} size={28} />
              </span>
              <div className={styles.entryBody}>
                <span className={styles.entryMeta}>
                  <span className={styles.entryAuthor}>{c.author_name || 'Unknown'}</span>{' '}
                  {formatRelative(c.created_at)}
                </span>
                <span className={styles.entryText}>{c.body}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.composer}>
        <Textarea
          value={draft}
          onChange={(_, d) => setDraft(d.value)}
          rows={3}
          placeholder="Ask a question, or add something the council should know"
          aria-label="Your message"
          data-testid="discussion-input"
        />
        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!draft.trim() || create.isPending}
            onClick={send}
            data-testid="discussion-send"
          >
            {create.isPending ? 'Sending…' : 'Send'}
          </Button>
          <span className={styles.note}>Messages are permanent — they cannot be edited.</span>
        </div>
        {error && (
          <span className={styles.error} data-testid="discussion-error">
            {error}
          </span>
        )}
      </div>
    </section>
  )
}
